import { useEffect, useRef, useState } from 'react';
import { analyticsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCellValue, formatDateTime } from '@/lib/utils';
import {
  chartTooltipStyle,
  commonCartesianGridProps,
  commonLegendProps,
  commonXAxisProps,
  commonYAxisProps,
  formatTrendTick,
  formatTrendTooltipLabel,
  getTrendXAxisInterval,
} from '@/lib/chartTheme';
import { ThumbsUp, ThumbsDown, MessageSquare, Globe, TrendingUp, ChevronDown, ListChecks, Search } from 'lucide-react';
import { PageBanner } from '@/components/PageBanner';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';
import { PageHeader } from '@/components/PageHeader';
import { RegionalMapView } from '@/components/RegionalMapView';
import { KpiCard as StatCard } from '@/components/KpiCard';
import { DetailTableRangeTabs, TrendRangeTabs } from '@/components/TrendRangeTabs';
import { AnalyticsTabBar } from '@/components/AnalyticsTabBar';
import { DataTablePagination, DataTableToolbar } from '@/components/DataTableControls';
import { ViewModeToggle } from '@/components/ViewModeToggle';
import { EmptyState } from '@/components/EmptyState';
import { MarkdownContent } from '@/components/MarkdownContent';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

function RegionalSentimentCard({ region, positive_pct, total }) {
  return (
    <div className="bg-card border border-border rounded-xl p-6 hover:shadow-md transition-shadow overflow-hidden">
      <div className="text-lg font-semibold text-foreground mb-4">{region}</div>
      <div className="flex items-baseline gap-2 mb-4">
        <div className="text-4xl font-bold text-success">{positive_pct}%</div>
        <div className="text-sm text-muted-foreground">positive</div>
      </div>
      <div className="text-sm text-muted-foreground">{total} ratings</div>
      <div className="mt-3 h-2 bg-secondary rounded-full overflow-hidden">
        <div 
          className="h-full bg-success rounded-full transition-all"
          style={{ width: `${positive_pct}%` }}
        />
      </div>
    </div>
  );
}

function FeedbackRow({ feedback, isOpen, onToggle }) {
  const isPositive = feedback.rating === 'thumbs_up';
  
  return (
    <Collapsible open={!!isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger className="w-full">
        <span className="sr-only">{isPositive ? 'Positive feedback' : 'Negative feedback'}</span>
        <div className="grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-3 p-4 sm:flex sm:gap-4 hover:bg-secondary/50 transition-colors cursor-pointer border-b border-border">
          <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0"
            style={{ backgroundColor: isPositive ? 'hsl(var(--success) / 0.2)' : 'hsl(var(--destructive) / 0.2)' }}
          >
            {isPositive ? (
              <ThumbsUp className="w-4 h-4 text-success" />
            ) : (
              <ThumbsDown className="w-4 h-4 text-destructive" />
            )}
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="text-sm text-muted-foreground">{formatDateTime(feedback.timestamp)}</div>
            <div className="text-sm font-medium text-foreground break-words line-clamp-2 sm:line-clamp-none sm:truncate">{feedback.query || 'No query recorded'}</div>
          </div>
          <div className="col-start-2 row-start-2 flex flex-wrap gap-2 text-xs text-muted-foreground sm:flex-shrink-0">
            <span className="bg-secondary px-2 py-1 rounded">{formatCellValue(feedback.region)}</span>
            <span className="bg-secondary px-2 py-1 rounded">{formatCellValue(feedback.country)}</span>
          </div>
          <ChevronDown className={`col-start-3 row-start-1 w-5 h-5 text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="bg-secondary p-4 border-b border-border">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Query</h4>
              <p className="text-sm text-foreground break-words">{feedback.query || 'No query recorded'}</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Answer</h4>
              <MarkdownContent
                text={feedback.response || feedback.answer}
                className="text-muted-foreground"
              />
              {!feedback.response && !feedback.answer && (
                <p className="text-sm text-muted-foreground">No response recorded</p>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span><strong>Region:</strong> {formatCellValue(feedback.region)}</span>
            <span><strong>Country:</strong> {formatCellValue(feedback.country)}</span>
          </div>
          {feedback.comment && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">User Comment</h4>
              <MarkdownContent text={feedback.comment} className="text-foreground italic" />
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function FeedbackCard({ feedback, isOpen, onToggle }) {
  const isPositive = feedback.rating === 'thumbs_up';

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!!isOpen}
      className="w-full rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-colors hover:bg-secondary/40"
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: isPositive ? 'hsl(var(--success) / 0.16)' : 'hsl(var(--destructive) / 0.16)' }}
        >
          {isPositive ? (
            <ThumbsUp className="h-4 w-4 text-success" />
          ) : (
            <ThumbsDown className="h-4 w-4 text-destructive" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">{formatDateTime(feedback.timestamp)}</p>
            <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
          <p className="mt-2 line-clamp-2 font-medium text-foreground">{feedback.query || 'No query recorded'}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span className="rounded bg-secondary px-2 py-1">{formatCellValue(feedback.region)}</span>
            <span className="rounded bg-secondary px-2 py-1">{formatCellValue(feedback.country)}</span>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="mt-4 space-y-3 border-t border-border pt-4 text-sm">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Answer</p>
            <MarkdownContent text={feedback.response || feedback.answer} className="text-muted-foreground" />
            {!feedback.response && !feedback.answer && (
              <p className="text-muted-foreground">No response recorded</p>
            )}
          </div>
          {feedback.comment && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">User Comment</p>
              <MarkdownContent text={feedback.comment} className="text-foreground italic" />
            </div>
          )}
        </div>
      )}
    </button>
  );
}

export default function FeedbackPage() {
  const { hasRole } = useAuth();
  const canViewResponseLog = hasRole(['admin', 'approver', 'analyst']);
  const [data, setData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [detailWindow, setDetailWindow] = useState('7d');
  const [trendWindow, setTrendWindow] = useState('7d');
  const [mapWindow, setMapWindow] = useState('7d');
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [openItems, setOpenItems] = useState({});
  const [activeTab, setActiveTab] = useState('details');
  const [detailsViewMode, setDetailsViewMode] = useState('list');
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [feedbackPageSize, setFeedbackPageSize] = useState(10);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasLoadedRef.current) {
        setLoading(true);
      }
      try {
        const result = await analyticsAPI.getFeedback(detailWindow);
        setData(result);
      } catch (err) {
        console.error('Failed to fetch feedback data:', err);
      } finally {
        hasLoadedRef.current = true;
        setLoading(false);
      }
    };
    fetchData();
  }, [detailWindow]);

  useEffect(() => {
    let cancelled = false;
    const fetchTrendData = async () => {
      setTrendLoading(true);
      try {
        const result = await analyticsAPI.getFeedback(trendWindow);
        if (cancelled) return;
        setTrendData(result);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch feedback trend data:', err);
      }
      setTrendLoading(false);
    };
    fetchTrendData();
    return () => {
      cancelled = true;
    };
  }, [trendWindow]);

  useEffect(() => {
    let cancelled = false;
    const fetchMapData = async () => {
      if (activeTab !== 'world') return;

      setMapLoading(true);
      try {
        const result = await analyticsAPI.getFeedback(mapWindow);
        if (cancelled) return;
        setMapData(result);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch feedback map data:', err);
        setMapData(null);
      }
      setMapLoading(false);
    };
    fetchMapData();
    return () => {
      cancelled = true;
    };
  }, [activeTab, mapWindow]);

  const toggleItem = (id) => {
    setOpenItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDetailWindowChange = (value) => {
    setDetailWindow(value);
    setFeedbackPage(1);
  };

  const handleFeedbackSearchChange = (value) => {
    setFeedbackSearch(value);
    setFeedbackPage(1);
  };

  const handleSentimentFilterChange = (value) => {
    setSentimentFilter(value);
    setFeedbackPage(1);
  };

  const handleDetailsViewModeChange = (value) => {
    setDetailsViewMode(value);
    setFeedbackPage(1);
  };

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="7xl"
        banner
        bannerPageKey="conversations-feedback"
        kpis={4}
        tabs
        sections={2}
        sectionClassName="h-80"
        data-testid="feedback-loading"
      />
    );
  }

  const { executive_snapshot, regional_sentiment, country_sentiment, trend, responses } = data || {};
  const chartTrend = trendData?.trend || trend || [];
  const mapRegionalSentiment = mapData?.regional_sentiment || regional_sentiment || [];
  const mapCountrySentiment = mapData?.country_sentiment || country_sentiment || [];
  const feedbackPageSizeOptions = [10, 25, 50];
  const filteredResponses = (responses || []).filter((feedback) => {
    const matchesSentiment = sentimentFilter === 'all'
      || (sentimentFilter === 'positive' && feedback.rating === 'thumbs_up')
      || (sentimentFilter === 'negative' && feedback.rating !== 'thumbs_up');

    const query = feedbackSearch.trim().toLowerCase();
    const matchesSearch = !query || [
      feedback.query,
      feedback.response,
      feedback.answer,
      feedback.comment,
      feedback.region,
      feedback.country,
    ].some((value) => String(value || '').toLowerCase().includes(query));

    return matchesSentiment && matchesSearch;
  });
  const feedbackTotalPages = Math.max(Math.ceil(filteredResponses.length / feedbackPageSize), 1);
  const safeFeedbackPage = Math.min(feedbackPage, feedbackTotalPages);
  const pagedResponses = filteredResponses.slice(
    (safeFeedbackPage - 1) * feedbackPageSize,
    safeFeedbackPage * feedbackPageSize
  );

  return (
    <PageShell maxWidth="7xl" contentClassName="space-y-8" data-testid="feedback-page">
      {/* Page Banner */}
      <PageBanner pageKey="conversations-feedback" />
      
      <PageHeader title="Feedback" description="User sentiment and response quality metrics" />

      {/* KPI strip */}
      <section>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Rated Interactions"
            value={executive_snapshot?.rated_interactions || 0}
            icon={MessageSquare}
          />
          <StatCard
            title="Positive Sentiment"
            value={`${executive_snapshot?.positive_pct || 0}%`}
            subtitle={(
              <span className="inline-flex items-center gap-1.5">
                <span>{executive_snapshot?.positive_count || 0}</span>
                <ThumbsUp className="h-4 w-4 text-success" aria-hidden="true" />
                <span>{executive_snapshot?.negative_count || 0}</span>
                <ThumbsDown className="h-4 w-4 text-destructive" aria-hidden="true" />
              </span>
            )}
            icon={ThumbsUp}
            color="emerald"
          />
          <StatCard
            title="Net Feedback Score"
            value={executive_snapshot?.net_feedback_score?.toFixed(2) || '0.00'}
            subtitle="Normalized score"
            icon={TrendingUp}
            color="blue"
          />
          <StatCard
            title="Countries Represented"
            value={`${executive_snapshot?.countries_represented || 0}+`}
            subtitle="Feedback coverage"
            icon={Globe}
            color="amber"
          />
        </div>
        <div className="mt-4 bg-secondary rounded-xl p-4 text-sm text-muted-foreground">
          <strong>Net Feedback Score</strong> summarizes overall user sentiment by balancing positive and negative feedback across all Eva interactions in the selected time window.
        </div>
      </section>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <AnalyticsTabBar
          tabs={[
            { value: 'details', label: 'Details', icon: ListChecks },
            { value: 'trends', label: 'Trends', icon: TrendingUp },
            { value: 'world', label: 'World', icon: Globe },
          ]}
        />

        <TabsContent value="details">
          {canViewResponseLog ? (
            <Card className="bg-card border-border shadow-sm">
              <CardHeader className="gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold text-foreground">Feedback Details</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Individual Eva interactions where users provided feedback. Click to expand.
                  </p>
                </div>
                <DetailTableRangeTabs value={detailWindow} onChange={handleDetailWindowChange} className="w-full lg:w-auto lg:min-w-[360px]" />
              </CardHeader>
              <CardContent className="space-y-4">
                <DataTableToolbar
                  searchValue={feedbackSearch}
                  onSearchChange={handleFeedbackSearchChange}
                  searchPlaceholder="Search feedback..."
                  searchTestId="feedback-search"
                  className="border-0 bg-transparent p-0"
                >
                  <Select value={sentimentFilter} onValueChange={handleSentimentFilterChange}>
                    <SelectTrigger className="w-full sm:w-44" data-testid="feedback-sentiment-filter">
                      <SelectValue placeholder="Sentiment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sentiment</SelectItem>
                      <SelectItem value="positive">Positive</SelectItem>
                      <SelectItem value="negative">Negative</SelectItem>
                    </SelectContent>
                  </Select>
                  <ViewModeToggle value={detailsViewMode} onChange={handleDetailsViewModeChange} />
                </DataTableToolbar>
                <div>
                  {(responses || []).length === 0 ? (
                    <EmptyState
                      icon={MessageSquare}
                      title="No feedback yet"
                      description="Feedback details will appear here once employees rate Eva responses."
                      variant="plain"
                    />
                  ) : filteredResponses.length === 0 ? (
                    <EmptyState
                      icon={Search}
                      title="No matching feedback"
                      description="Try changing your search or sentiment filter."
                      variant="plain"
                    />
                  ) : (
                    detailsViewMode === 'card' ? (
                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        {pagedResponses.map((feedback) => (
                          <FeedbackCard
                            key={feedback.id}
                            feedback={feedback}
                            isOpen={openItems[feedback.id]}
                            onToggle={() => toggleItem(feedback.id)}
                          />
                        ))}
                      </div>
                    ) : (
                      pagedResponses.map((feedback) => (
                        <FeedbackRow
                          key={feedback.id}
                          feedback={feedback}
                          isOpen={openItems[feedback.id]}
                          onToggle={() => toggleItem(feedback.id)}
                        />
                      ))
                    )
                  )}
                </div>
                {filteredResponses.length > 0 && (
                  <DataTablePagination
                    page={safeFeedbackPage}
                    totalPages={feedbackTotalPages}
                    totalItems={filteredResponses.length}
                    pageSize={feedbackPageSize}
                    pageSizeOptions={feedbackPageSizeOptions}
                    onPageChange={setFeedbackPage}
                    onPageSizeChange={(value) => {
                      setFeedbackPageSize(value);
                      setFeedbackPage(1);
                    }}
                    itemLabel="feedback items"
                    pageSizeTestId="feedback-page-size"
                  />
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-border shadow-sm">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                Feedback response details are available to admins, approvers, and analysts.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="trends">
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-lg font-semibold text-foreground">Feedback Trends</CardTitle>
              <TrendRangeTabs value={trendWindow} onChange={setTrendWindow} className="w-full sm:w-auto sm:min-w-[320px]" />
            </CardHeader>
            <CardContent>
              {trendLoading ? (
                <Skeleton className="h-72 w-full rounded-xl" data-testid="feedback-trend-loading" />
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartTrend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid {...commonCartesianGridProps} />
                      <XAxis
                        dataKey="date"
                        {...commonXAxisProps}
                        interval={getTrendXAxisInterval(trendWindow)}
                        tickFormatter={(value) => formatTrendTick(value, trendWindow)}
                      />
                      <YAxis {...commonYAxisProps} />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        labelFormatter={(value) => formatTrendTooltipLabel(value, trendWindow)}
                      />
                      <Legend {...commonLegendProps} />
                      <Bar dataKey="positive" fill="#10b981" name="Thumbs up" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="negative" fill="#ef4444" name="Thumbs down" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-4">
                Daily trend of rated interactions over the selected window. Green represents thumbs up, red represents thumbs down.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="world">
          <RegionalMapView
            title="Feedback Map"
            description="Feedback volume and sentiment by region"
            regions={(mapRegionalSentiment || []).map((region) => ({
              region: region.region,
              value: region.total,
              secondaryValue: region.positive_pct,
            }))}
            countries={(mapCountrySentiment || []).map((country) => ({
              country: country.country,
              region: country.region,
              value: country.total,
              secondaryValue: country.positive_pct,
            }))}
            metricLabel="Ratings"
            secondaryLabel="Positive %"
            colorScale="green"
            timeRangeValue={mapWindow}
            onTimeRangeChange={setMapWindow}
            loading={mapLoading}
          />

          <Card className="mt-6 bg-card border-border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-foreground">Regional Feedback</CardTitle>
            </CardHeader>
            <CardContent>
              {mapLoading ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="feedback-regional-loading">
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-40 w-full rounded-xl" />
                  ))}
                </div>
              ) : (mapRegionalSentiment || []).length === 0 ? (
                <EmptyState
                  icon={Globe}
                  title="No regional feedback data yet"
                  description="Regional feedback signals will appear once employees rate answers."
                  variant="plain"
                  compact
                />
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {mapRegionalSentiment?.map((region) => (
                    <RegionalSentimentCard
                      key={region.region}
                      region={region.region}
                      positive_pct={region.positive_pct}
                      total={region.total}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
